<template>
  <!--
    Compact language switcher — sits in the AppBar on every page so a
    customer is never more than one tap away from changing language.
    Drives the global setLocale() helper (vue-i18n + localStorage + html
    lang/dir). Closes on outside click and Escape.

    `onDark` switches the trigger's text/hover colors so the same
    component works in the white mobile AppBar and the dark blue desktop
    AppBar without two separate styles.
  -->
  <div class="relative" ref="root">
    <button
      type="button"
      class="inline-flex items-center gap-1 h-9 px-2.5 rounded-lg text-sm font-semibold transition select-none"
      :class="onDark ? 'text-white hover:bg-white/10' : 'text-slate-700 hover:bg-slate-100'"
      :aria-label="`${$t('language.languageLabel')}: ${currentLabel}`"
      :title="currentLabel"
      :aria-expanded="open"
      aria-haspopup="listbox"
      @click.stop="open = !open"
    >
      <!-- Globe icon — universal "language" affordance. Slightly larger
           on mobile because it's the only visible cue when the screen is
           narrow enough that we drop the code letters. -->
      <svg class="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="9"/>
        <path stroke-linecap="round" d="M3 12h18"/>
        <path stroke-linecap="round" d="M12 3c2.5 2.7 4 5.9 4 9s-1.5 6.3-4 9c-2.5-2.7-4-5.9-4-9s1.5-6.3 4-9z"/>
      </svg>
      <span class="uppercase tracking-wider text-xs leading-none">{{ currentCode }}</span>
      <svg class="h-3 w-3 opacity-60 shrink-0" viewBox="0 0 12 12" fill="currentColor"><path d="M6 9L2 4h8z"/></svg>
    </button>

    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <ul
        v-if="open"
        role="listbox"
        :aria-label="$t('language.languageLabel')"
        class="absolute right-0 top-full mt-1 z-50 min-w-[180px] bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden origin-top-right py-1"
      >
        <li v-for="opt in options" :key="opt.code" role="presentation">
          <button
            type="button"
            role="option"
            :aria-selected="opt.code === currentCode"
            class="w-full px-3 py-2.5 text-sm text-left flex items-center gap-2 transition"
            :class="opt.code === currentCode
              ? 'text-brand-700 font-semibold bg-brand-50/70'
              : 'text-slate-700 hover:bg-slate-50'"
            @click="pick(opt.code)"
          >
            <span class="text-[10px] font-bold uppercase tracking-wider w-6 text-center text-slate-400">{{ opt.code }}</span>
            <span class="flex-1">{{ opt.label }}</span>
            <svg
              v-if="opt.code === currentCode"
              class="h-4 w-4 text-brand-600 shrink-0"
              viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
            ><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/></svg>
          </button>
        </li>
      </ul>
    </transition>
  </div>
</template>

<script>
import i18n, { setLocale, supportedLocales } from "@/i18n";

// Each language is listed by its own native name — the standard
// accessible practice (Wikipedia, Airbnb). Showing "Arabic" in English
// or French to an Arabic speaker is worse than showing "العربية".
const NATIVE_LABELS = {
  fr: "Français",
  en: "English",
  ar: "العربية",
  es: "Español",
};

export default {
  name: "LanguageSwitcher",
  props: {
    /** Use the light variant on dark backgrounds (desktop blue AppBar). */
    onDark: { type: Boolean, default: false },
  },
  data() {
    return { open: false };
  },
  computed: {
    /** Reactive read of vue-i18n's current locale ref. */
    currentCode() { return i18n.global.locale.value; },
    currentLabel() { return NATIVE_LABELS[this.currentCode] || this.currentCode; },
    options() {
      return supportedLocales.map((code) => ({
        code,
        label: NATIVE_LABELS[code] || code,
      }));
    },
  },
  mounted() {
    document.addEventListener("click", this.handleOutsideClick);
    document.addEventListener("keydown", this.handleEsc);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleOutsideClick);
    document.removeEventListener("keydown", this.handleEsc);
  },
  methods: {
    pick(code) {
      setLocale(code);
      this.open = false;
    },
    handleOutsideClick(e) {
      if (this.open && this.$refs.root && !this.$refs.root.contains(e.target)) {
        this.open = false;
      }
    },
    handleEsc(e) {
      if (e.key === "Escape" && this.open) this.open = false;
    },
  },
};
</script>
